import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import PageContainer from '../components/PageContainer';
import PageHero from '../components/PageHero';
import { CATEGORIES, getCategoryList } from '../data/categories';
import { 
  Camera, Palette, Coffee, Music, Sparkles, Mic, Calendar, 
  Baby, Building, Users, Car, Printer, Drama, Image, BookOpen, 
  MoreHorizontal, ChevronRight, Search
} from 'lucide-react';

// Icon-Mapping für alle Kategorien
const CATEGORY_ICONS = {
  'Fotografen & Videografen': Camera,
  'Make-up & Styling': Palette,
  'Catering & Foodtrucks': Coffee,
  'Deko & Eventdesign': Sparkles,
  'Technik & Equipment-Verleih': Mic,
  'Musik & Entertainment': Music,
  'Eventplanung & Organisation': Calendar,
  'Kinder- & Familien-Entertainment': Baby,
  'Locations & Räume': Building,
  'Personal & Service': Users,
  'Fahrservice & Transport': Car,
  'Branding & Druck': Printer,
  'Show & Performance': Drama,
  'Foto- / Fun-Module': Image,
  'Weiterbildung & Workshops': BookOpen,
  'Sonstiges & Spezial': MoreHorizontal
};

// Beschreibungen für alle Kategorien
const CATEGORY_DESCRIPTIONS = {
  'Fotografen & Videografen': 'Professionelle Foto- und Videodienstleister für Hochzeiten, Events, Business und Social Media.',
  'Make-up & Styling': 'Experten für Brautstyling, Event-Make-up, Hairstyling und professionelle Looks für jeden Anlass.',
  'Catering & Foodtrucks': 'Von Full-Service-Catering bis Foodtrucks – kulinarische Highlights für dein Event.',
  'Deko & Eventdesign': 'Kreative Raumgestaltung, Floristik und Lichtdesign für unvergessliche Atmosphären.',
  'Technik & Equipment-Verleih': 'Professionelle Event-Technik: Ton, Licht, Bühnenbau, LED-Wände und Equipment.',
  'Musik & Entertainment': 'DJs, Live-Bands, Solokünstler, Zauberer, Comedy und spektakuläre Showacts.',
  'Eventplanung & Organisation': 'Professionelle Hochzeits- und Eventplaner für perfekt organisierte Veranstaltungen.',
  'Kinder- & Familien-Entertainment': 'Kinderanimateure, Clowns, Hüpfburgen und magische Unterhaltung für die Kleinen.',
  'Locations & Räume': 'Die perfekte Location für Hochzeiten, Firmenfeiern, Tagungen und private Events.',
  'Personal & Service': 'Servicekräfte, Barkeeper, Kellner, Hostessen und Security für professionellen Service.',
  'Fahrservice & Transport': 'Chauffeurservice, Limousinen, Oldtimer und Shuttle für stilvolle Anreise.',
  'Branding & Druck': 'Werbetechnik, Eventbeschilderung, Einladungen und individuelle Giveaways.',
  'Show & Performance': 'Tänzer, Akrobatik, Feuershows, Moderatoren und professionelle Bühnenshows.',
  'Foto- / Fun-Module': 'Fotoboxen, 360°-Videobooths, Green-Screen und interaktive Erlebnisstationen.',
  'Weiterbildung & Workshops': 'Barista-, Cocktail-, Tanz- und Make-up-Workshops für Teamevents und Weiterbildung.',
  'Sonstiges & Spezial': 'Hochzeitsredner, Live-Maler, Karikaturisten und außergewöhnliche Special-Effects.'
};

const CategoriesPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showSubcategories, setShowSubcategories] = useState({});

  const categories = getCategoryList();

  const handleCategoryClick = (category) => {
    navigate(`/search?category=${encodeURIComponent(category)}`);
  };

  const toggleSubcategories = (category, e) => {
    e.stopPropagation();
    setShowSubcategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br bg-[#F4F2F2]">
      <SEO
        title="Alle Event-Kategorien"
        description="Entdecke alle 16 Kategorien für Event-Dienstleister auf EventConnect: Von Fotografen über DJs bis Catering – finde den perfekten Dienstleister für dein Event."
        keywords="Event Kategorien, Dienstleister Übersicht, Hochzeit, Fotograf, DJ, Catering, Entertainment"
        url="/kategorien"
      />
      <Navbar user={user} onLogout={onLogout} />

      {/* Hero Section */}
      <PageHero
        title="Alle Kategorien auf EventConnect"
        subtitle="Wähle eine Kategorie, um passende Dienstleister für dein Event zu finden. Ob DJ, Fotograf oder Catering – hier findest du alle Dienstleister übersichtlich nach Bereichen sortiert."
      />
      
      <div className="pb-16">
        <PageContainer size="large">

          {/* Category Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {categories.map((category) => {
              const IconComponent = CATEGORY_ICONS[category] || MoreHorizontal;
              const subcategories = CATEGORIES[category] || [];
              const isExpanded = showSubcategories[category];

              return (
                <div
                  key={category}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border-2 border-transparent hover:border-[#2B2142] flex flex-col"
                >
                  {/* Category Header */}
                  <div
                    onClick={() => handleCategoryClick(category)}
                    className="p-6 cursor-pointer flex flex-col flex-1"
                  >
                    {/* Icon */}
                    <div className="w-14 h-14 bg-gradient-to-br from-[#FFF0D1] to-[#F4F2F2] rounded-xl flex items-center justify-center mb-4 group-hover:from-[#2B2142] group-hover:to-[#3D3158] transition-all duration-300">
                      <IconComponent className="w-7 h-7 text-[#2B2142] group-hover:text-white transition-colors duration-300" />
                    </div>

                    {/* Category Name */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#2B2142] transition-colors">
                      {category}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                      {CATEGORY_DESCRIPTIONS[category]}
                    </p>

                    {/* Subcategories Count */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-500">
                        {subcategories.length} Unterkategorien
                      </span>
                      <button
                        onClick={(e) => toggleSubcategories(category, e)}
                        className="text-xs text-[#2B2142] hover:text-[#1E1730] font-semibold flex items-center gap-1"
                      >
                        {isExpanded ? 'Weniger' : 'Mehr'}
                        <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </button>
                    </div>

                    {/* Subcategories (Expandable) */}
                    {isExpanded && (
                      <div className="mb-4 pt-3 border-t border-gray-100">
                        <div className="flex flex-wrap gap-2">
                          {subcategories.map((sub) => (
                            <span
                              key={sub}
                              className="px-2 py-1 bg-[#FFF0D1]/50 text-[#2B2142] rounded-lg text-xs font-medium"
                            >
                              {sub}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* CTA Button */}
                    <button
                      className="mt-auto w-full py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
                    >
                      <Search className="w-4 h-4" />
                      Dienstleister anzeigen
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="bg-gradient-to-r from-[#2B2142] to-[#3D3158] rounded-3xl p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Nicht die richtige Kategorie gefunden?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Nutze unsere erweiterte Suche oder kontaktiere uns direkt
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <button
                onClick={() => navigate('/search')}
                className="px-8 py-4 bg-white text-[#2B2142] hover:bg-gray-100 font-semibold rounded-xl text-lg transition-colors shadow-lg"
              >
                Zur erweiterten Suche
              </button>
              <button
                onClick={() => navigate('/for-customers')}
                className="px-8 py-4 bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold rounded-xl text-lg transition-colors"
              >
                Mehr erfahren
              </button>
            </div>
          </div>
        </PageContainer>
      </div>

      <Footer />
    </div>
  );
};

export default CategoriesPage;
